import React from 'react';
import { Link, Route } from 'react-router-dom';
import { Translate, TranslateHTML, TranslateContext } from 'core/translate';
import { SvgIcon } from 'core/icons';
import { Dagger } from '../../common/notifications/NotificationsTrigger';
import Odometer from './Odometer';
import PriceSummaryThumbnail from './PriceSummaryThumbnail';
import { useNotifications } from '../../common/notifications/NotificationsContext';
import { useModelInfo } from '../../common/ModelInfoContext';

const PriceInfoIcon = ({ disclaimer, isSummary }) => {
  const { disabled, showNamed } = useNotifications();

  return (
    <button
      onClick={event => {
        if (isSummary && !disabled) {
          showNamed(disclaimer, event);
        }
      }}>
      <SvgIcon name="info" />
    </button>
  );
};

const SeeDealerDisclaimer = ({ disclaimer, isSummary }) => {
  const { disabled, showNamed } = useNotifications();

  return (
    <span
      onClick={event => {
        if (isSummary && !disabled) {
          showNamed(disclaimer, event);
        }
      }}>
      <Dagger className="price-summary-bar__dagger" />
    </span>
  );
};

const computeDisplayInfo = (pricing, modelInfo) => {
  if (pricing.employeePrice) {
    if (modelInfo.isCanada) {
      return {
        label: 'total.price',
        value: pricing.totalMsrp,
        disclaimer: 'total-price'
      };
    }
    return {
      label: 'net.employee.price',
      value: pricing.netEmployeePrice,
      disclaimer: 'msrp'
    };
  }
  if (modelInfo.isFamilyFirstUS) {
    if (pricing.incentives.total || pricing.incentives.value) {
      return {
        label: 'net.price.after.offers',
        value: pricing.netEmployeePrice,
        disclaimer: 'msrp'
      };
    }
    if (!pricing.incentives.total || pricing.incentives.value) {
      return {
        label: 'total.vehicle.price',
        value: pricing.netEmployeePrice,
        disclaimer: 'msrp'
      };
    }
  }
  return {
    label: 'net.price',
    value: pricing.netPrice,
    disclaimer: 'msrp'
  };
};

export const PriceContent = props => {
  const {
    pricing,
    disableExpansion,
    displayThumbnail,
    images,
    interiorIndex,
    exteriorIndex,
    switchThumbnail,
    profileOnly,
    isSummary
  } = props;
  const modelInfo = useModelInfo();

  const isSeeDealer = pricing.isSeeDealer;
  const { label, value, disclaimer } = computeDisplayInfo(pricing, modelInfo);

  if (isSeeDealer) {
    return (
      <div className="price-summary-bar__see-dealer">
        <Translate value="unknown.price.text" />
        {disableExpansion ? null : (
          <SeeDealerDisclaimer disclaimer={disclaimer} isSummary={isSummary} />
        )}
      </div>
    );
  }

  return (
    <div className="price-summary-bar__price-content">
      <PriceSummaryThumbnail
        images={images}
        interiorIndex={interiorIndex}
        exteriorIndex={exteriorIndex}
        displayThumbnail={displayThumbnail}
        switchThumbnail={switchThumbnail}
        profileOnly={profileOnly}
      />

      <div
        className={`price-summary-bar__price-container  ${
          displayThumbnail ? 'price-summary-bar__price-container--stack' : ''
        }`}>
        <Odometer value={value} locale={modelInfo.locale} />
        <div className="price-summary-bar__price-label">
          <Translate value={label} />
        </div>
        {disableExpansion ? null : (
          <PriceInfoIcon disclaimer={disclaimer} isSummary={isSummary} />
        )}
      </div>
    </div>
  );
};

const SaveSendLink = ({ isSummary }) => {
  if (!isSummary) {
    return null;
  }
  return (
    <Link to="/summary/share" className="saved-notification__link">
      <SvgIcon name="heart" className="svg-icon--heart" />
      <span className="saved-notification__label">
        <Translate value="saved" />
      </span>
    </Link>
  );
};

const ExpanderButton = ({ isSummary, isExpanded }) => {
  if (isSummary) {
    return null;
  }

  return (
    <TranslateContext.Consumer>
      {translate => (
        <button
          data-dtm={'price summary bar'}
          aria-label={translate('component.pricing.price.summary')}
          aria-expanded={isExpanded}>
          <SvgIcon
            name="chevron-up"
            className={`price-summary-bar__toggle-icon stat-expand ${
              isExpanded ? '' : 'hidden'
            } `}
          />
          <SvgIcon
            name="chevron-down"
            className={`price-summary-bar__toggle-icon stat-expand ${
              isExpanded ? 'hidden' : ''
            } `}
          />
        </button>
      )}
    </TranslateContext.Consumer>
  );
};

const PriceSummaryBarAction = props => {
  const { disableExpansion, isExpanded, isSummary } = props;

  return (
    <div
      className={`price-summary-bar__actions ${
        isSummary ? 'price-summary-bar__saved' : ''
      }`}>
      {disableExpansion ? null : (
        <ExpanderButton isSummary={isSummary} isExpanded={isExpanded} />
      )}
      <SaveSendLink isSummary={isSummary} />
    </div>
  );
};

const PriceSummaryTitle = ({ bodyStyle, year, driveType, config, trim }) => {
  const { display, priceSummaryBarDisclaimer } = window.appResources;

  const summaryBarDisclaimer = display.enablePriceSummaryBarDisclaimer
    ? priceSummaryBarDisclaimer
    : '';

  return (
    <div className="price-summary-bar__text-container">
      <div className="price-summary-bar__title-content">
        <span className="price-summary-bar__title">
          <TranslateHTML
            value="component.price.summary.bar.title"
            bodyStyle={bodyStyle}
            year={year}
          />
        </span>

        <span className="show-for-large">
          <Translate
            value="component.trim.attributes.label"
            driveType={driveType}
            config={config}
            trim={trim}
            year={year}
          />
        </span>
        <span className="price-summary-bar__low-availbility">
          &nbsp;
          {summaryBarDisclaimer}
        </span>
      </div>
    </div>
  );
};

const PriceSummaryBarContent = props => {
  const {
    disableExpansion,
    bodyStyle,
    year,
    driveType,
    config,
    trim,
    switchThumbnail,
    displayThumbnail,
    pricing,
    images,
    interiorIndex,
    exteriorIndex,
    profileOnly,
    isSummary,
    notifications
  } = props;

  return (
    <div
      className={`price-summary-bar__content ${
        disableExpansion ? 'price-summary-bar__content--disabled-collapse' : ''
      }`}>
      <div className="price-summary-bar__brand">
        <Translate value="price.summary.brand" />
      </div>
      <div className="price-summary-bar__content-inner">
        <PriceSummaryTitle
          bodyStyle={bodyStyle}
          year={year}
          driveType={driveType}
          config={config}
          trim={trim}
        />

        <PriceContent
          switchThumbnail={switchThumbnail}
          displayThumbnail={displayThumbnail}
          pricing={pricing}
          disableExpansion={disableExpansion}
          images={images}
          interiorIndex={interiorIndex}
          exteriorIndex={exteriorIndex}
          profileOnly={profileOnly}
          isSummary={isSummary}
          notifications={notifications}
        />
      </div>
    </div>
  );
};

const PriceSummaryBar = props => {
  const {
    isExpanded,
    pricing,
    year,
    bodyStyle,
    driveType,
    config,
    trim,
    onToggleExpanded,
    images,
    interiorIndex,
    exteriorIndex,
    switchThumbnail,
    displayThumbnail,
    disableExpansion,
    notifications
  } = props;

  return (
    <Route path="/summary">
      {({ match: summaryMatch }) => {
        const isSummary = !!summaryMatch;
        return (
          <div
            className={`price-summary-bar stat-image-link ${
              disableExpansion ? 'price-summary-bar--disabled' : ''
            } ${isSummary ? 'price-summary-bar--summary-page-only' : ''}`}
            data-dtm="net-price"
            onClick={() => {
              if (!isSummary) {
                onToggleExpanded();
              }
            }}>
            <Route path="/config">
              {({ match: configMatch }) => (
                <PriceSummaryBarContent
                  bodyStyle={bodyStyle}
                  year={year}
                  driveType={driveType}
                  config={config}
                  trim={trim}
                  switchThumbnail={switchThumbnail}
                  displayThumbnail={displayThumbnail}
                  pricing={pricing}
                  disableExpansion={disableExpansion}
                  images={images}
                  interiorIndex={interiorIndex}
                  exteriorIndex={exteriorIndex}
                  profileOnly={!!configMatch}
                  isSummary={isSummary}
                  notifications={notifications}
                />
              )}
            </Route>

            <PriceSummaryBarAction
              disableExpansion={disableExpansion}
              isExpanded={isExpanded}
              isSummary={isSummary}
            />
          </div>
        );
      }}
    </Route>
  );
};

export default PriceSummaryBar;
